<script lang="ts" setup>
    import { ref, watch, onMounted, computed } from "vue";
    import { IComboOptionGroup } from "../lib/Models/ComboModel";

    const emit = defineEmits<{
        (e: "onChange", selectedItem: any):void,
        (e: "update:selected", selectedItem: any):void,
    }>();

    defineExpose({onComboChange});

    const selected = ref<string | number>();
    const comboList = ref<IComboOptionGroup[]>([]);
    const selectedTooltip = ref<string>();

    const props = withDefaults(defineProps<{
        // Placeholder text to be displayed inside text control if no values
        optionList: Array<any>,
        optionValue?: string,
        optionDescription?: string,
        optionTooltip?: string,
        optionHeader?: string,
        includeHeader?: boolean,
        includeBlank?: boolean,
        selected?: string | number,
        size?: number,
        disabled?: boolean
    }>(), {
        optionValue: "value",
        optionDescription: "description",
        optionTooltip: "tooltip",
        optionHeader: "header",
        includeBlank: false,
        includeHeader: false,
        disabled: false
    });

    onMounted(() => {
        selected.value = props.selected;
        loadCombo();
        setTooltip();
    });

    watch(() => props.selected, (_new, _old) => {
        if (_new !== _old) {
            selected.value = _new;
            setTooltip();
        }
    });

    watch(() => props.optionList, (_new, _old) => {
        if (_new !== _old) {
            loadCombo();
            setTooltip(); 
        }
    });
    function loadCombo() : void {
        comboList.value = [];
        if (!props.includeHeader) {
            comboList.value.push({groupName: "", optionList: []});
            props.optionList.forEach(function (optionItem) {
                comboList.value[0].optionList.push({value: optionItem[props.optionValue], description: optionItem[props.optionDescription], tooltip: optionItem[props.optionTooltip]});
            });
        } 
        else {
            props.optionList.forEach(function (optionItem) {
                let selectedItem = comboList.value.find(item => item.groupName == optionItem[props.optionHeader ?? ""]);
                if (!selectedItem) {
                    comboList.value.push({groupName: optionItem[props.optionHeader ?? ""], optionList: [{value: optionItem[props.optionValue], description: optionItem[props.optionDescription], tooltip: optionItem[props.optionTooltip]}]});
                } else {
                    selectedItem?.optionList.push({value: optionItem[props.optionValue], description: optionItem[props.optionDescription], tooltip: optionItem[props.optionTooltip]});
                }
            });
        }
    }
    const groupEnabled = computed<boolean>(() => {
        if (comboList.value.length == 0) {
            return false;
        }
        else if (comboList.value.length == 1) {
            return comboList.value[0].groupName != "" ? true : false;
        }
        return true;
    });

    const controlTooltip = computed<string>(() => {
        if (selectedTooltip.value != "")
            return selectedTooltip.value ?? "";
        return "";
    });

    function isSelected(value: string | number) {
        if (!selected.value)
            return false;
        return selected.value == value ? true : false;
    }

    function onComboChange() {
        const selectedItem = props.optionList.find(item => item[props.optionValue] == selected.value);
        if (selectedItem) {
            selectedTooltip.value = selectedItem[props.optionTooltip];
            emit("onChange", selectedItem);
            emit("update:selected", selectedItem[props.optionValue]);
        }
        else {
            selectedTooltip.value = "";
            emit("onChange", undefined);
            emit("update:selected", undefined);
        }
    }

    function setTooltip() {
        if (!selected.value)
            return;
        const selectedItem = props.optionList.find(item => item[props.optionValue] == selected.value);
        if (selectedItem)
            selectedTooltip.value = selectedItem[props.optionTooltip];
    }

</script>

<template>
    <select class="form-select form-select-sm small_text_start small_text_other" v-model="selected" :size="props.size" :disabled="props.disabled" :title="controlTooltip" @change="onComboChange()">
        <option v-if="props.includeBlank" value=""></option>
        <optgroup v-if="groupEnabled" v-for="group in comboList" :label="group.groupName">
            <option v-for="item in group.optionList" :selected="isSelected(item.value)" :value="item.value" :title="item.tooltip">{{ item.description }}</option>
        </optgroup>
        <template v-if="!groupEnabled" v-for="group in comboList">
            <option v-for="item in group.optionList" :selected="isSelected(item.value)" :value="item.value" :title="item.tooltip">{{ item.description }}</option>
        </template>
    </select>
</template>